import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { EnvironmentProviders, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, Provider, provideZoneChangeDetection } from "@angular/core";
import { ActivatedRoute, provideRouter, Route, Router } from "@angular/router";
import { JWT_OPTIONS, JwtConfig, JwtModule } from "@auth0/angular-jwt";
import { provideShellService } from "@tylertech/incode-module";
import { configureLogger, getLoggerFor, LogLevel } from "@tylertech/logging";
import { catchError, EMPTY, finalize, Observable, switchMap, take } from "rxjs";
import { ErpProJwtConfig } from "./erpProJwtConfig";
import { GlobalErrorHandler } from "./errorHandler";
import { logMonitoredApiCalls, trapAndPollGatewayTimeouts } from "./interceptors";
import { LIVE_CHAT_ADAPTER } from "./interfaces";
import { coreRoutes } from "./routes";
import * as services from "./services";
import "./tylerComponents";
import "./tylerIcons";

const coreModuleLog = getLoggerFor("CoreModule");

function jwtOptionsFactory(): JwtConfig
{
    return new ErpProJwtConfig();
}

function redirectIfDoubleSlashes(): void
{
    if (location.pathname?.includes('//'))
    {
        // angular router cannot handle double slash
        location.href = location.pathname.replace(/\/\//g, '/') + location.search;
    }
}

/**
 * This function will replace the default serialization logic for Date object
 * Deserialization isn't affected
 */
function replaceDateToJSON(): void
{
    Date.prototype.toJSON = function (this: Date): string
    {
        // If it's an invalid date, return now
        if (isNaN(this as unknown as number))
        {
            return new Date()
                .toISOString();
        }

        // If it's midnight in local time, use unspecified format 2021-08-09T00:00:00 without time zone info,
        // instead of default ISO/UTC format 2021-08-09T05:00:00.000Z
        if (this.toTimeString().substring(0, 8) === "00:00:00")
        {
            // e.g. 2021-08-09T00:00:00
            return this.getFullYear()
                + "-"
                + ("0" + (this.getMonth() + 1)).slice(-2)
                + "-"
                + ("0" + this.getDate()).slice(-2)
                + "T00:00:00";
        }
        else
        {
            // e.g. 2021-08-09T06:00:00.000Z
            return this.toISOString();
        }
    };
}

replaceDateToJSON();

function coreAppInitializer(): void | Observable<unknown> | Promise<unknown>
{
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const systemInfoService = inject(services.SystemInfoService);
    const securityService = inject(services.SecurityService);
    const accessLogHandlerService = inject(services.AccessLogHandlerService);
    const storageService = inject(services.StorageService);
    const walkMeService = inject(services.WalkMeService);
    redirectIfDoubleSlashes();

    walkMeService.forceExpireWalkMeCookies();

    return systemInfoService.getSystemInfo()
        .pipe(
            take(1),
            switchMap((systemInfo: services.SystemInfoExtended) =>
            {
                coreModuleLog.information(`Initializing AccessLogHandlerService`);
                accessLogHandlerService.start(systemInfo.IsTest());

                coreModuleLog.information(`Initializing WalkMe`);
                walkMeService.initializeScript(systemInfo.Product);

                coreModuleLog.information(`Initializing SecurityService`);
                return securityService.initialize();
            }),
            catchError((error) =>
            {
                if (services.isHttpResponseError(error)
                    && "ExceptionMessage" in error.error
                    && error.error.ExceptionMessage === "Unable to connect to the remote server")
                {
                    const redirectParams = securityService.buildRedirectParams(activatedRoute.snapshot);

                    router.navigate([services.SecurityRoute.Login], { queryParams: redirectParams });
                }

                return EMPTY;
            }),
            finalize(() =>
            {
                coreModuleLog.information(`app initialized`);
            })
        );
}

export function provideCore(appRoutes: Array<Route> = [], logLevel: LogLevel = LogLevel.DEBUG): Array<Provider | EnvironmentProviders>
{
    configureLogger(logLevel);

    return [
        provideZoneChangeDetection(),
        provideBrowserGlobalErrorListeners(),
        provideAppInitializer(coreAppInitializer),
        provideHttpClient(
            withInterceptors([
                logMonitoredApiCalls,
                trapAndPollGatewayTimeouts,
            ]),
            withInterceptorsFromDi(),
        ),
        importProvidersFrom(
            JwtModule.forRoot({
                jwtOptionsProvider: {
                    provide: JWT_OPTIONS,
                    useFactory: jwtOptionsFactory,
                },
            })),
        provideRouter(coreRoutes.concat(appRoutes)),
        provideShellService(),
        services.AccessLogHandlerService,
        services.ApiService,
        services.AppService,
        services.AuthorizeService,
        services.BrowserService,
        services.BrowserNotificationService,
        services.CachedRepositoryService,
        services.CacheHelperService,
        services.ChatService,
        services.ContextMenuService,
        services.DefaultEmailService,
        services.DispatchService,
        services.ExceptionService,
        services.FeatureFlagHelperService,
        services.FileService,
        services.FormMetadataService,
        services.HelpService,
        services.KeyHandlerService,
        services.LoggingService,
        services.NavigationService,
        services.NavMenuService,
        services.NotificationService,
        services.OmniBarService,
        services.OmniSearchService,
        services.OpenIdService,
        services.ProcessStepperService,
        services.ReportFormService,
        services.RepositoryService,
        services.RouteInfoService,
        services.SecurityService,
        services.SetupViewService,
        services.StateReportingService,
        services.StorageService,
        services.SystemInfoService,
        services.ThemeService,
        services.ToastService,
        services.UrlService,
        services.UserProfileService,
        services.UserService,
        services.WalkMeService,
        services.AiAgentService,
        services.CxBridgeService,
        services.FoundryChatAdapter,
        services.TylerAssistantSidebarService,
        // CxBridgeService drives its chat surface through this token; rebind to
        // swap the Foundry chatbot for a different LiveChatAdapter implementation.
        // useExisting: the bridge must share the instance SmartPulseComponent
        // attaches the chatbot element to, not get a second copy.
        { provide: LIVE_CHAT_ADAPTER, useExisting: services.FoundryChatAdapter },
        { provide: ErrorHandler, useClass: GlobalErrorHandler, },
    ];
}
